import { create } from 'zustand';
import type { Connector, Agent, KnowledgeBase, Channel, LlmModel, AgentFunction, FormSchema as AtlasFormSchema, NodeFormDto } from '@flow-studio/api';

interface ConnectorConfigState {
  // Dialog state
  isOpen: boolean;
  loading: boolean;
  saving: boolean;
  testing: boolean;
  error: string | null;

  // Connector data
  connector: Connector | null;
  connectorId: number;
  processElementId?: number;

  // Form schema state
  formSchema: AtlasFormSchema | null;
  configForms: NodeFormDto[];
  activeConfigFormId: number | null;
  configFormValues: Map<number, Record<string, any>>;

  // Form values
  formRendererValues: Record<string, any>;
  formRendererInitialValues: Record<string, any>;
  // Full config loaded from backend — stable fallback across tab switches
  loadedConfigValues: Record<string, any>;

  // Credentials
  credentialID: number | null;

  // Entities
  agents: Agent[];
  knowledgeBases: KnowledgeBase[];
  channels: Channel[];
  llmModels: LlmModel[];
  agentFunctions: AgentFunction[];

  // Entity selections
  agentId: number | null;
  knowledgeBaseId: number | null;
  channelId: number | null;
  llmModelId: number | null;
  agentFunctionId: number | null;

  // Entity loading states
  loadingAgents: boolean;
  loadingKBs: boolean;
  loadingChannels: boolean;
  loadingLLMs: boolean;
  loadingFunctions: boolean;

  // Entity error states
  agentError: string | null;
  kbError: string | null;
  channelError: string | null;
  llmError: string | null;
  functionError: string | null;

  // Search/filter state
  agentSearch: string;
  agentShowResults: boolean;
  kbSearch: string;
  kbShowResults: boolean;
  channelSearch: string;
  channelShowResults: boolean;
  llmSearch: string;
  llmShowResults: boolean;
  functionSearch: string;
  functionShowResults: boolean;

  // Entity add dialog
  openAddDialog: 'agent' | 'knowledgeBase' | 'channel' | 'llmModel' | 'agentFunction' | null;

  // Tabs
  activeTab: string;
  visibleTabs: string[];

  // Test result
  testResult: any | null;

  // Documentation
  docLabel: string;
  docDescription: string;

  // Memory type
  memoryType: 'bizfirst' | 'custom';

  // Agent Function tab metadata
  agentFunctionDisplayName: string;
  agentFunctionDisplayOrder: number;
  agentFunctionLabel: string;
  agentFunctionFlowPath: string;
  agentFunctionAlias: string;
  agentFunctionProfileName: string;

  // Actions
  setConnectorId: (id: number) => void;
  setOpen: (isOpen: boolean) => void;
  setLoading: (loading: boolean) => void;
  setSaving: (saving: boolean) => void;
  setTesting: (testing: boolean) => void;
  setError: (error: string | null) => void;
  setConnector: (connector: Connector | null) => void;
  setFormSchema: (schema: AtlasFormSchema | null) => void;
  setConfigForms: (forms: NodeFormDto[]) => void;
  setActiveConfigFormId: (id: number | null) => void;
  setConfigFormValues: (id: number, values: Record<string, any>) => void;
  setFormRendererValues: (values: Record<string, any>) => void;
  setFormRendererInitialValues: (values: Record<string, any>) => void;
  setLoadedConfigValues: (values: Record<string, any>) => void;
  setCredentialID: (id: number | null) => void;
  setAgents: (agents: Agent[]) => void;
  setKnowledgeBases: (kbs: KnowledgeBase[]) => void;
  setChannels: (channels: Channel[]) => void;
  setLlmModels: (models: LlmModel[]) => void;
  setAgentFunctions: (funcs: AgentFunction[]) => void;
  setAgentId: (id: number | null) => void;
  setKnowledgeBaseId: (id: number | null) => void;
  setChannelId: (id: number | null) => void;
  setLlmModelId: (id: number | null) => void;
  setAgentFunctionId: (id: number | null) => void;
  setLoadingAgents: (loading: boolean) => void;
  setLoadingKBs: (loading: boolean) => void;
  setLoadingChannels: (loading: boolean) => void;
  setLoadingLLMs: (loading: boolean) => void;
  setLoadingFunctions: (loading: boolean) => void;
  setAgentError: (error: string | null) => void;
  setKbError: (error: string | null) => void;
  setChannelError: (error: string | null) => void;
  setLlmError: (error: string | null) => void;
  setFunctionError: (error: string | null) => void;
  setAgentSearch: (search: string) => void;
  setAgentShowResults: (show: boolean) => void;
  setKbSearch: (search: string) => void;
  setKbShowResults: (show: boolean) => void;
  setChannelSearch: (search: string) => void;
  setChannelShowResults: (show: boolean) => void;
  setLlmSearch: (search: string) => void;
  setLlmShowResults: (show: boolean) => void;
  setFunctionSearch: (search: string) => void;
  setFunctionShowResults: (show: boolean) => void;
  setOpenAddDialog: (dialog: 'agent' | 'knowledgeBase' | 'channel' | 'llmModel' | 'agentFunction' | null) => void;
  setActiveTab: (tab: string) => void;
  setVisibleTabs: (tabs: string[]) => void;
  setTestResult: (result: any) => void;
  setDocLabel: (label: string) => void;
  setDocDescription: (desc: string) => void;
  setMemoryType: (type: 'bizfirst' | 'custom') => void;
  setAgentFunctionDisplayName: (val: string) => void;
  setAgentFunctionDisplayOrder: (val: number) => void;
  setAgentFunctionLabel: (val: string) => void;
  setAgentFunctionFlowPath: (val: string) => void;
  setAgentFunctionAlias: (val: string) => void;
  setAgentFunctionProfileName: (val: string) => void;

  // Bulk operations
  reset: () => void;
  resetFormState: () => void;
}

const initialState = {
  isOpen: false,
  loading: false,
  saving: false,
  testing: false,
  error: null,
  connector: null,
  connectorId: 0,
  formSchema: null,
  configForms: [],
  activeConfigFormId: null,
  configFormValues: new Map(),
  formRendererValues: {},
  formRendererInitialValues: {},
  loadedConfigValues: {},
  credentialID: null,
  agents: [],
  knowledgeBases: [],
  channels: [],
  llmModels: [],
  agentFunctions: [],
  agentId: null,
  knowledgeBaseId: null,
  channelId: null,
  llmModelId: null,
  agentFunctionId: null,
  loadingAgents: false,
  loadingKBs: false,
  loadingChannels: false,
  loadingLLMs: false,
  loadingFunctions: false,
  agentError: null,
  kbError: null,
  channelError: null,
  llmError: null,
  functionError: null,
  agentSearch: '',
  agentShowResults: false,
  kbSearch: '',
  kbShowResults: false,
  channelSearch: '',
  channelShowResults: false,
  llmSearch: '',
  llmShowResults: false,
  functionSearch: '',
  functionShowResults: false,
  openAddDialog: null,
  activeTab: 'configuration',
  visibleTabs: [],
  testResult: null,
  docLabel: '',
  docDescription: '',
  memoryType: 'bizfirst' as const,
  agentFunctionDisplayName: '',
  agentFunctionDisplayOrder: 0,
  agentFunctionLabel: '',
  agentFunctionFlowPath: '',
  agentFunctionAlias: '',
  agentFunctionProfileName: '',
};

export const useConnectorConfigStore = create<ConnectorConfigState>((set) => ({
  ...initialState,

  setConnectorId: (id) => set({ connectorId: id }),
  setOpen: (isOpen) => set({ isOpen }),
  setLoading: (loading) => set({ loading }),
  setSaving: (saving) => set({ saving }),
  setTesting: (testing) => set({ testing }),
  setError: (error) => set({ error }),
  setConnector: (connector) => set({ connector }),
  setFormSchema: (schema) => set({ formSchema: schema }),
  setConfigForms: (forms) => set({ configForms: forms }),
  setActiveConfigFormId: (id) => set({ activeConfigFormId: id }),
  setConfigFormValues: (id, values) => set((state) => ({
    configFormValues: new Map(state.configFormValues).set(id, values),
  })),
  setFormRendererValues: (values) => set({ formRendererValues: values }),
  setFormRendererInitialValues: (values) => set({ formRendererInitialValues: values }),
  setLoadedConfigValues: (values) => set({ loadedConfigValues: values }),
  setCredentialID: (id) => set({ credentialID: id }),
  setAgents: (agents) => set({ agents }),
  setKnowledgeBases: (kbs) => set({ knowledgeBases: kbs }),
  setChannels: (channels) => set({ channels }),
  setLlmModels: (models) => set({ llmModels: models }),
  setAgentFunctions: (funcs) => set({ agentFunctions: funcs }),
  setAgentId: (id) => set({ agentId: id }),
  setKnowledgeBaseId: (id) => set({ knowledgeBaseId: id }),
  setChannelId: (id) => set({ channelId: id }),
  setLlmModelId: (id) => set({ llmModelId: id }),
  setAgentFunctionId: (id) => set({ agentFunctionId: id }),
  setLoadingAgents: (loading) => set({ loadingAgents: loading }),
  setLoadingKBs: (loading) => set({ loadingKBs: loading }),
  setLoadingChannels: (loading) => set({ loadingChannels: loading }),
  setLoadingLLMs: (loading) => set({ loadingLLMs: loading }),
  setLoadingFunctions: (loading) => set({ loadingFunctions: loading }),
  setAgentError: (error) => set({ agentError: error }),
  setKbError: (error) => set({ kbError: error }),
  setChannelError: (error) => set({ channelError: error }),
  setLlmError: (error) => set({ llmError: error }),
  setFunctionError: (error) => set({ functionError: error }),
  setAgentSearch: (search) => set({ agentSearch: search }),
  setAgentShowResults: (show) => set({ agentShowResults: show }),
  setKbSearch: (search) => set({ kbSearch: search }),
  setKbShowResults: (show) => set({ kbShowResults: show }),
  setChannelSearch: (search) => set({ channelSearch: search }),
  setChannelShowResults: (show) => set({ channelShowResults: show }),
  setLlmSearch: (search) => set({ llmSearch: search }),
  setLlmShowResults: (show) => set({ llmShowResults: show }),
  setFunctionSearch: (search) => set({ functionSearch: search }),
  setFunctionShowResults: (show) => set({ functionShowResults: show }),
  setOpenAddDialog: (dialog) => set({ openAddDialog: dialog }),
  setActiveTab: (tab) => set({ activeTab: tab }),
  setVisibleTabs: (tabs) => set({ visibleTabs: tabs }),
  setTestResult: (result) => set({ testResult: result }),
  setDocLabel: (label) => set({ docLabel: label }),
  setDocDescription: (desc) => set({ docDescription: desc }),
  setMemoryType: (type) => set({ memoryType: type }),
  setAgentFunctionDisplayName: (val) => set({ agentFunctionDisplayName: val }),
  setAgentFunctionDisplayOrder: (val) => set({ agentFunctionDisplayOrder: val }),
  setAgentFunctionLabel: (val) => set({ agentFunctionLabel: val }),
  setAgentFunctionFlowPath: (val) => set({ agentFunctionFlowPath: val }),
  setAgentFunctionAlias: (val) => set({ agentFunctionAlias: val }),
  setAgentFunctionProfileName: (val) => set({ agentFunctionProfileName: val }),

  reset: () => set(initialState),
  resetFormState: () => set({
    // Clear connector so stale data from a previously-opened node never bleeds in.
    // formSchema is intentionally NOT cleared here to avoid a blank-form flash
    // before initializeForm re-loads it.
    connector: null,
    configForms: [],
    activeConfigFormId: null,
    formRendererValues: {},
    formRendererInitialValues: {},
    loadedConfigValues: {},
    configFormValues: new Map(),
    credentialID: null,
    agentId: null,
    knowledgeBaseId: null,
    channelId: null,
    llmModelId: null,
    agentFunctionId: null,
    agentFunctionDisplayName: '',
    agentFunctionDisplayOrder: 0,
    agentFunctionLabel: '',
    agentFunctionFlowPath: '',
    agentFunctionAlias: '',
    agentFunctionProfileName: '',
  }),
}));
